export type PhysicsDomainId =
  'electromagnetism' | 'thermal' | 'momentum' | 'circular-gravity' | 'modern-physics';

export interface LabControl {
  key: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  initial: number;
}

export interface LabExample {
  id: string;
  name: string;
  values: Record<string, number>;
  note: string;
}

export interface LabReadout {
  label: string;
  value: number;
  unit: string;
}

export interface LabResult {
  readouts: LabReadout[];
  summary: string;
  working: string[];
  plot: { x: number; y: number }[];
  xLabel: string;
  yLabel: string;
}

export interface LabExperiment {
  id: string;
  name: string;
  question: string;
  controls: LabControl[];
  examples: LabExample[];
  assumptions: string;
  source: string;
  calculate: (values: Record<string, number>) => LabResult;
  prediction: {
    question: string;
    options: string[];
    answer: number;
    why: string;
  };
  task: string;
}

export interface PhysicsDomain {
  id: PhysicsDomainId;
  name: string;
  hint: string;
  experiments: LabExperiment[];
}

export const PHYSICS_DOMAIN_VERSION = '0.3.0';

export function control(
  key: string,
  label: string,
  unit: string,
  min: number,
  max: number,
  initial: number,
  step = 1,
): LabControl {
  return { key, label, unit, min, max, initial, step };
}

export function readout(label: string, value: number, unit: string): LabReadout {
  return { label, value: Object.is(value, -0) ? 0 : value, unit };
}

export function curve(max: number, calculate: (x: number) => number): { x: number; y: number }[] {
  return Array.from({ length: 41 }, (_, index) => {
    const x = (max * index) / 40;
    return { x, y: calculate(x) };
  });
}

export function display(value: number): string {
  if (Math.abs(value) < 1e-12) return '0';
  return Math.abs(value) >= 1e5 || Math.abs(value) < 0.001
    ? value.toExponential(3)
    : Number(value.toPrecision(4)).toString();
}

export interface LabState {
  experiment: LabExperiment;
  values: Record<string, number>;
  example: string;
  notices: string[];
}

export function readLabState(domain: PhysicsDomain, params: URLSearchParams): LabState {
  const notices: string[] = [];
  const requested = params.get('experiment');
  const experiment =
    domain.experiments.find((item) => item.id === requested) ?? domain.experiments[0];
  if (requested && requested !== experiment.id)
    notices.push('Unknown experiment. The first experiment is shown.');
  const requestedExample = params.get('example');
  const preset =
    experiment.examples.find((item) => item.id === requestedExample) ?? experiment.examples[0];
  const custom = requestedExample === 'custom';
  if (requestedExample && !custom && requestedExample !== preset.id)
    notices.push('Unknown example. The first example is shown.');
  const values: Record<string, number> = {};
  for (const field of experiment.controls) {
    const fallback = preset.values[field.key] ?? field.initial;
    const raw = custom ? params.get(field.key) : null;
    const parsed = raw === null || raw.trim() === '' ? fallback : Number(raw);
    const bounded = Number.isFinite(parsed)
      ? Math.min(field.max, Math.max(field.min, parsed))
      : fallback;
    const discreteTurns = field.key === 'turns' && !Number.isInteger(bounded);
    values[field.key] = discreteTurns ? Math.round(bounded) : bounded;
    if (discreteTurns)
      notices.push(`Coil turns was rounded to the whole number ${values[field.key]}.`);
    if (raw !== null && (raw.trim() === '' || !Number.isFinite(parsed) || parsed !== bounded))
      notices.push(`${field.label} was reset or limited to ${display(bounded)} ${field.unit}.`);
  }
  return {
    experiment,
    values,
    example: custom ? 'custom' : preset.id,
    notices,
  };
}

export function labParams(
  experiment: LabExperiment,
  values: Record<string, number>,
): Record<string, string> {
  return {
    experiment: experiment.id,
    example: 'custom',
    ...Object.fromEntries(experiment.controls.map(({ key }) => [key, String(values[key])])),
  };
}
